import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { rutaFichaCliente } from '@/lib/ruta-cliente'

// Tipo directo de la BD
type SearchIndexRow = Database['public']['Tables']['search_index']['Row']

// Tipo para uso en la aplicación (alias para claridad)
export interface SearchResult {
  id: string
  entidad_tipo: string
  entidad_id: string
  titulo: string
  contenido: string | null // subtitle/CUIT/código
  descripcion?: string | null // tipo persona + condición fiscal
  categoria: string | null
  workspace: string | null
  organizacion_id: string
  updated_at: string | null
}

// Adaptador de BD a tipo de aplicación
function adaptarResultado(row: SearchIndexRow): SearchResult {
  return {
    id: row.id,
    entidad_tipo: row.entidad_tipo,
    entidad_id: row.entidad_id,
    titulo: row.titulo,
    contenido: row.contenido,
    descripcion: row.categoria, // Usamos categoria para la descripción adicional
    categoria: row.categoria,
    workspace: row.workspace,
    organizacion_id: row.organizacion_id || '',
    updated_at: row.updated_at,
  }
}

export interface SearchResultAgrupado {
  tipo: string
  label: string
  resultados: SearchResult[]
}

const TIPO_LABELS: Record<string, string> = {
  clientes: 'Clientes',
  movimientos: 'Movimientos',
  tareas: 'Tareas',
  cuentas: 'Cuentas',
  servicios: 'Servicios',
  presupuestos: 'Presupuestos',
  // Legacy - mantener por compatibilidad
  personas: 'Personas',
  documentos: 'Documentos',
  vencimientos_fiscales: 'Vencimientos',
}

const TIPO_RUTAS: Record<string, (id: string) => string> = {
  clientes: (id) => rutaFichaCliente(id),
  movimientos: (id) => `/movimientos?id=${id}`,
  tareas: (id) => `/tareas?id=${id}`,
  cuentas: (id) => `/cuentas?id=${id}`,
  servicios: (id) => `/servicios?id=${id}`,
  presupuestos: (id) => `/presupuestos?id=${id}`,
  // Legacy - mantener por compatibilidad
  personas: (id) => `/personas/${id}`,
  documentos: (id) => `/documentos?id=${id}`,
  vencimientos_fiscales: (id) => `/calendario?id=${id}`,
}

/**
 * Configs de text search con las que se consulta `search_index.search_vector`, que se genera con
 * `spanish`. Sin config, PostgREST toma `default_text_search_config`, que en el servidor es `english`.
 * - `spanish`: la misma raíz y el mismo agudo de vocales que el índice («Contables», «gestion»).
 * - `simple`: no descarta stopwords. Con `spanish` sola, «de la» da una query vacía y no encuentra
 *   «Delacroix, Laura»; con `simple`, `'de':* & 'la':*` matchea esos prefijos.
 * Los resultados se unen: cada config cubre lo que la otra pierde. El orden es la prioridad al truncar:
 * `simple` va primero porque exige todos los prefijos pedidos. `spanish` descarta las stopwords, y con
 * «de ju» busca solo `'ju':*`: sus coincidencias parciales no pueden desplazar a las completas.
 */
export const SEARCH_TEXT_CONFIGS = ['simple', 'spanish'] as const

/**
 * Prepara el término del buscador para los filtros de PostgREST.
 *
 * - NFC: el índice guarda el texto compuesto. Una «ñ» que llega como «n» + tilde no matchearía.
 *   Los acentos no se sacan: la config `spanish` ya quita el agudo de las vocales en el índice y en
 *   la query, y no toca la ñ ni la ü, así que sacarlas en el front rompería el match.
 * - `tsQuery`: cada palabra va entre comillas simples de tsquery, con `'` y `\` escapados. Así los
 *   signos (`( ) | & ! :`) son texto, y `to_tsquery` la tokeniza igual que `to_tsvector`. `:*` busca
 *   por prefijo. Una palabra que es solo signos o stopwords no aporta lexemas y no da error.
 * - `regexValue`: el término como regex de `imatch` (`~*`), con los metacaracteres escapados. No se
 *   usa ILIKE porque PostgREST convierte todo `*` de un `ilike` en `%`, y un `*` del usuario no
 *   tendría forma de ser literal. Va entre comillas dobles, con `\` y `"` escapados, para que la
 *   coma y los paréntesis no corten el `or()`.
 */
export const prepararTerminoBusqueda = (query: string): { tsQuery: string; regexValue: string } | null => {
  const termino = query.normalize('NFC').trim()
  if (!termino) return null

  const tsQuery = termino
    .split(/\s+/)
    .map((palabra) => `'${palabra.replace(/\\/g, '\\\\').replace(/'/g, "''")}':*`)
    .join(' & ')

  const regex = termino.replace(/[\\^$.|?*+()[\]{}]/g, '\\$&')
  const regexValue = `"${regex.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`

  return { tsQuery, regexValue }
}

export const searchService = {
  /**
   * Busca en el índice por full-text (una consulta por config) y por texto en titulo y contenido
   * @param query Texto a buscar
   * @param organizacionId ID de la organización
   * @param workspace Filtrar por workspace (opcional)
   * @param limit Límite de resultados (default 20)
   */
  async buscar(client: SupabaseClient<Database>, 
    query: string,
    organizacionId: string,
    workspace?: string,
    limit: number = 20
  ): Promise<SearchResult[]> {
    const termino = prepararTerminoBusqueda(query)
    if (!termino) return []

    const consultaBase = () => {
      const base = client
        .from('search_index')
        .select('*')
        .eq('organizacion_id', organizacionId)
        .order('updated_at', { ascending: false })
        .limit(limit)
      return workspace ? base.or(`workspace.is.null,workspace.eq.${workspace}`) : base
    }

    // El full-text tokeniza el término y descarta el `*`: con «Plan*» encontraría «Plan anual». Si el
    // usuario escribió un asterisco, la búsqueda es literal y la resuelve solo `imatch`.
    const configs = query.includes('*') ? [] : SEARCH_TEXT_CONFIGS

    const [textResult, ...fullTextResults] = await Promise.all([
      // Texto en titulo y contenido (para CUIT, CLI-XXX, etc.)
      consultaBase().or(`titulo.imatch.${termino.regexValue},contenido.imatch.${termino.regexValue}`),
      ...configs.map((config) =>
        consultaBase().textSearch('search_vector', termino.tsQuery, { config })
      ),
    ])

    if (textResult.error) throw textResult.error

    // Un fallo del full-text no corta la búsqueda: la de texto corre igual
    for (const { error } of fullTextResults) {
      if (error) console.error('Búsqueda full-text fallida:', error)
    }

    // Combinar resultados únicos (priorizar full-text, en el orden de las configs)
    const seenIds = new Set<string>()
    const combinedResults: SearchIndexRow[] = []

    for (const row of [...fullTextResults, textResult].flatMap((r) => r.data || [])) {
      if (!seenIds.has(row.id)) {
        seenIds.add(row.id)
        combinedResults.push(row)
      }
    }

    // Adaptar y retornar
    return combinedResults.slice(0, limit).map(adaptarResultado)
  },

  /**
   * Busca y agrupa resultados por tipo de entidad
   */
  async buscarAgrupado(client: SupabaseClient<Database>,
    query: string,
    organizacionId: string,
    workspace?: string,
    limit: number = 30
  ): Promise<SearchResultAgrupado[]> {
    const resultados = await this.buscar(client, query, organizacionId, workspace, limit)

    // Agrupar por tipo
    const agrupados = new Map<string, SearchResult[]>()

    for (const resultado of resultados) {
      const tipo = resultado.entidad_tipo
      if (!agrupados.has(tipo)) {
        agrupados.set(tipo, [])
      }
      agrupados.get(tipo)!.push(resultado)
    }

    // Convertir a array ordenado por relevancia (cantidad de resultados)
    return Array.from(agrupados.entries())
      .map(([tipo, resultados]) => ({
        tipo,
        label: TIPO_LABELS[tipo] || tipo,
        resultados,
      }))
      .sort((a, b) => b.resultados.length - a.resultados.length)
  },

  /**
   * Obtiene la ruta de navegación para un resultado
   */
  obtenerRuta(resultado: SearchResult): string {
    const getRuta = TIPO_RUTAS[resultado.entidad_tipo]
    if (getRuta) {
      return getRuta(resultado.entidad_id)
    }
    return '/'
  },

  /**
   * Obtiene el label del tipo de entidad
   */
  obtenerLabelTipo(tipo: string): string {
    return TIPO_LABELS[tipo] || tipo
  },

  /**
   * Busca sugerencias rápidas (solo títulos, sin full-text)
   */
  async sugerencias(client: SupabaseClient<Database>, 
    query: string,
    organizacionId: string,
    limit: number = 5
  ): Promise<SearchResult[]> {
    if (!query.trim() || query.length < 2) return []

    const { data, error } = await client
      .from('search_index')
      .select('*')
      .eq('organizacion_id', organizacionId)
      .ilike('titulo', `%${query}%`)
      .order('updated_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return (data || []).map(adaptarResultado)
  },
}
